import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { computeRunHistoryStats } from 'redux/slices/pipelines/helpers/computeRunHistoryStats';

// OQ-3 resolved — last 10 runs, not a calendar window.
const RUN_HISTORY_WINDOW_SIZE = 10;

// Semantic theme colors only (NFR-Accessibility/AC-15b) — no hardcoded hex. `neutral` is a
// non-terminal run (AC-11a(iii)): not success, not error.
const BAR_COLOR_BY_STATE = {
	success: 'success.main',
	error: 'error.main',
	neutral: 'action.disabled'
};

// AC-11/AC-11a — run-history stats strip + sparkline, computed client-side from the
// already-fetched (unpaged) runs list — no additional request (NFR-Performance). The
// sparkline is hand-rolled themed `Box` bars: `recharts` is a dependency but MUST NOT be
// used here (it's a chart library for a handful of small rectangles).
const RunHistoryStats = ({ runs }) => {
	// AC-9 — zero runs: the strip is not rendered at all (the caller already gates this,
	// this guard is defensive/redundant).
	if (!runs || runs.length === 0) {
		return null;
	}

	const { windowCount, succeededCount, medianDurationLabel, bars } = computeRunHistoryStats(runs, RUN_HISTORY_WINDOW_SIZE);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 3,
				p: 2,
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1.5
			}}
		>
			<HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-hidden="true" />
			<Typography variant="body2" color="text.secondary">
				{`Last ${windowCount} run${windowCount === 1 ? '' : 's'} · ${succeededCount} succeeded · median ${medianDurationLabel}`}
			</Typography>
			<Box flex={1} />
			{/* Decorative — the sentence above already carries this as accessible text
			    (NFR-Accessibility: status is never colour-alone here either, since each bar's
			    meaning is already summarised in words). */}
			<Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
				{bars.map((bar, index) => (
					<Box
						key={bar.key ?? index}
						sx={{ width: 6, height: 16, borderRadius: 0.5, bgcolor: BAR_COLOR_BY_STATE[bar.state] ?? BAR_COLOR_BY_STATE.neutral }}
					/>
				))}
			</Box>
		</Box>
	);
};

export default React.memo(RunHistoryStats);

RunHistoryStats.propTypes = {
	runs: PropTypes.array
};
